"use client";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { motion } from "motion/react";

import VerificationCodeForm from "../../SignInSteps/VerificationCodeForm/VerificationCodeForm.tsx";

import RegisterForm from "./RegisterForm.tsx";

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher";
import AppLogo from "@/components/Logo";
import PATHS from "@/routes/paths.ts";

export default function RegisterSteps() {
  const [step, setStep] = useState<0 | 1>(0);
  const { t } = useTranslation("auth");

  const stepContent = useMemo(
    () => [
      {
        component: (
          <RegisterForm
            goToNextStep={() => {
              setStep(1);
            }}
          />
        ),
      },
      {
        component: <VerificationCodeForm />,
      },
    ],
    [], // Updated dependency array
  );

  const { component } = stepContent[step];
  const MotionCard = motion(Card);

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center">
      <div className="absolute top-10 right-10 z-50">
        <LanguageSwitcher />
      </div>
      <MotionCard
        layout
        className="bg-foreground/50 flex h-dvh w-screen max-w-xl flex-col items-center justify-center overflow-hidden border-none shadow-2xl backdrop-blur-xl max-md:max-w-screen md:h-[675px] md:max-w-2xl md:rounded-2xl"
        layoutId={"form"}
      >
        <CardHeader className="items-center justify-center md:pb-0">
          <CardTitle className="text-center">
            <AppLogo className="h-10" />
          </CardTitle>
          <motion.div
            layout
            animate={{ opacity: 0.05 }}
            className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-gradient-to-br from-green-400 to-blue-500 opacity-5"
            initial={{ opacity: 0.05 }}
            layoutId={"blue"}
            transition={{ layout: { duration: 0.75 } }}
          />
          <motion.div
            layout
            animate={{ opacity: 0.05 }}
            className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-gradient-to-tr from-purple-400 to-pink-600 opacity-5"
            initial={{ opacity: 0.05 }}
            layoutId={"purple"}
            transition={{ layout: { duration: 0.75 } }}
          />
        </CardHeader>
        <div className="relative">
          <CardContent className="w-full p-2! md:py-6">{component}</CardContent>
          <CardFooter className="flex flex-col justify-end md:pb-0">
            <Link className={"text-accent-foreground text-sm"} to={PATHS.SIGN_IN}>
              {t("signUp.hasAnAccount")}
            </Link>
            <div
              className={
                "text-muted-foreground mt-4 text-center text-sm sm:max-w-[75%] lg:max-w-[80%]"
              }
            >
              <Trans
                components={{
                  link1: (
                    <Link key={"1"} className="text-accent-foreground inline w-fit" to={PATHS.TERMS_OF_SERVICE} />
                  ),
                  link2: (
                    <Link key={"2"} className="text-accent-foreground inline w-fit" to={PATHS.PRIVACY_POLICY} />
                  ),
                }}
                i18nKey={"terms"}
                ns={"auth"}
              />
            </div>
          </CardFooter>
        </div>
      </MotionCard>
    </div>
  );
}
